// Friction: where alternative routes through a process split and join again, the slower routes take longer than
// the quickest. Written without the graph library so it can be tested; the analysis service applies it to a graph

// enough to compare the alternatives without enumerating every route through a large, heavily branched process
export const MAX_ROUTES = 50

// every route back from a node to where the process starts, each beginning with the node. A node already on a route
// isn't visited again, so a loop in the process ends the route
export const findRoutesBack = <T>(start: T, previous: (node: T) => T[], maxRoutes = MAX_ROUTES): T[][] => {
  const routes: T[][] = []
  const walk = (route: T[]) => {
    if (routes.length >= maxRoutes) return
    const next = previous(route[route.length - 1]).filter(p => !route.includes(p))
    if (next.length === 0) {
      routes.push(route)
      return
    }
    next.forEach(p => walk([...route, p]))
  }
  walk([start])
  return routes
}

export interface FrictionResult<T> {
  // the nodes on each route between where the routes split and the joining node, both left out
  segments: T[][]
  hours: number[]
  // hours on each route as a multiple of the quickest; 0 for the quickest
  friction: number[]
}

// for a node where routes join: finds the nearest node all its routes back pass through, and compares the time
// taken along each route from there. Undefined if the routes never meet, or there is only one
export const calculateFriction = <T>(routes: T[][], hours: (node: T) => number): FrictionResult<T> | undefined => {
  if (routes.length < 2) return undefined

  const [first, ...others] = routes
  // the first node, after the joining node itself, that every route reaches
  const splitIndex = first.findIndex((node, i) => i > 0 && others.every(r => r.includes(node)))
  if (splitIndex < 0) return undefined
  const split = first[splitIndex]

  const segments = routes.map(r => r.slice(1, r.indexOf(split)))
  const routeHours = segments.map(s => s.reduce((total, node) => total + (Number(hours(node)) || 0), 0))
  const quickest = Math.min(...routeHours)
  const friction = routeHours.map(h => (h === quickest || quickest <= 0) ? 0 : h / quickest)

  return { segments, hours: routeHours, friction }
}
